/**
 * Infinite Scroll for Shopify Collections
 * Replaces traditional pagination with seamless infinite loading
 * 
 * Usage: Automatically activates on collection pages with pagination
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    threshold: 0.8,           // Trigger when 80% down the page
    loadingDelay: 300,        // Debounce delay in ms
    scrollOffset: 300,        // Distance from bottom to trigger (px)
  };

  // State
  let isLoading = false;
  let hasMorePages = false;
  let nextPageUrl = null;
  let observer = null;

  /**
   * Initialize infinite scroll on page load
   */
  function init() {
    // Only run on collection and search pages
    if (!isCollectionPage()) {
      return;
    }

    // Check if pagination exists
    const pagination = document.querySelector('.pagination');
    if (!pagination) {
      return;
    }

    // Get next page URL
    const nextLink = pagination.querySelector('.next a');
    if (!nextLink) {
      return;
    }

    nextPageUrl = nextLink.getAttribute('href');
    hasMorePages = true;

    // Hide pagination - we'll use infinite scroll instead
    pagination.style.display = 'none';

    // Create loading indicator
    createLoadingIndicator();

    // Set up scroll observer
    setupScrollObserver();

    // Set up intersection observer for better performance
    setupIntersectionObserver();
  }

  /**
   * Check if we're on a collection or search page
   */
  function isCollectionPage() {
    return document.querySelector('[data-section-type="collection-grid"]') !== null;
  }

  /**
   * Create loading indicator element
   */
  function createLoadingIndicator() {
    const indicator = document.createElement('div');
    indicator.className = 'infinite-scroll-loading';
    indicator.innerHTML = `
      <div class="infinite-scroll-spinner">
        <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
          <circle cx="20" cy="20" r="18" fill="none" stroke="#000" stroke-width="2" opacity="0.2"/>
          <circle cx="20" cy="20" r="18" fill="none" stroke="#000" stroke-width="2" stroke-dasharray="90 150" stroke-linecap="round">
            <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="1s" values="0 20 20;360 20 20"/>
          </circle>
        </svg>
        <p>Laster flere produkter...</p>
      </div>
    `;
    indicator.style.display = 'none';
    indicator.style.textAlign = 'center';
    indicator.style.padding = '40px 0';
    indicator.style.fontSize = '14px';
    indicator.style.color = '#666';

    // Insert after product grid
    const grid = document.querySelector('.collection-grid__wrapper');
    if (grid && grid.parentNode) {
      grid.parentNode.insertBefore(indicator, grid.nextSibling);
    }
  }

  /**
   * Set up scroll observer (fallback method)
   */
  function setupScrollObserver() {
    let scrollTimeout;
    
    window.addEventListener('scroll', function() {
      // Debounce scroll events
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(function() {
        checkScrollPosition();
      }, CONFIG.loadingDelay);
    }, { passive: true });
  }

  /**
   * Set up intersection observer (modern method)
   */
  function setupIntersectionObserver() {
    // Create sentinel element at bottom of grid
    const sentinel = document.createElement('div');
    sentinel.className = 'infinite-scroll-sentinel';
    sentinel.style.height = '1px';
    sentinel.style.visibility = 'hidden';

    const grid = document.querySelector('.collection-grid__wrapper');
    if (grid && grid.parentNode) {
      grid.parentNode.insertBefore(sentinel, grid.nextSibling);
    }

    // Create intersection observer
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting && hasMorePages && !isLoading) {
            loadNextPage();
          }
        });
      }, {
        rootMargin: CONFIG.scrollOffset + 'px'
      });

      observer.observe(sentinel);
    }
  }

  /**
   * Check scroll position (fallback method)
   */
  function checkScrollPosition() {
    if (isLoading || !hasMorePages) {
      return;
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollPercentage = (scrollTop + windowHeight) / documentHeight;

    if (scrollPercentage >= CONFIG.threshold) {
      loadNextPage();
    }
  }

  /**
   * Load next page of products
   */
  function loadNextPage() {
    if (isLoading || !hasMorePages || !nextPageUrl) {
      return;
    }

    isLoading = true;
    showLoadingIndicator();

    // Fetch next page
    fetch(nextPageUrl)
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then(function(html) {
        appendProducts(html);
        hideLoadingIndicator();
        isLoading = false;
      })
      .catch(function(error) {
        console.error('Infinite scroll error:', error);
        hideLoadingIndicator();
        isLoading = false;
        // Show error message
        showErrorMessage();
      });
  }

  /**
   * Append products from fetched HTML
   */
  function appendProducts(html) {
    // Create temporary element to parse HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Find product grid in fetched page
    const newProducts = temp.querySelector('.collection-grid__wrapper .grid');
    if (!newProducts) {
      hasMorePages = false;
      return;
    }

    // Find current product grid
    const currentGrid = document.querySelector('.collection-grid__wrapper .grid');
    if (!currentGrid) {
      hasMorePages = false;
      return;
    }

    // Append new products
    const products = newProducts.querySelectorAll('.grid__item');
    products.forEach(function(product) {
      currentGrid.appendChild(product.cloneNode(true));
    });

    // Check for next page
    const newPagination = temp.querySelector('.pagination .next a');
    if (newPagination) {
      nextPageUrl = newPagination.getAttribute('href');
      hasMorePages = true;
    } else {
      hasMorePages = false;
      showEndMessage();
    }

    // Reinitialize any scripts that need to run on new products
    reinitializeScripts();
  }

  /**
   * Reinitialize scripts for new products
   */
  function reinitializeScripts() {
    // Trigger custom event for theme to reinitialize product-specific features
    if (window.theme && typeof window.theme.reinitProductGridItem === 'function') {
      window.theme.reinitProductGridItem();
    }

    // Re-run AOS (Animate On Scroll) if present
    if (window.AOS) {
      window.AOS.refresh();
    }

    // Shopify theme scripts
    if (window.Shopify && window.Shopify.ProductReviews) {
      window.Shopify.ProductReviews.loadProducts();
    }
  }

  /**
   * Show loading indicator
   */
  function showLoadingIndicator() {
    const indicator = document.querySelector('.infinite-scroll-loading');
    if (indicator) {
      indicator.style.display = 'block';
    }
  }

  /**
   * Hide loading indicator
   */
  function hideLoadingIndicator() {
    const indicator = document.querySelector('.infinite-scroll-loading');
    if (indicator) {
      indicator.style.display = 'none';
    }
  }

  /**
   * Show error message
   */
  function showErrorMessage() {
    const grid = document.querySelector('.collection-grid__wrapper');
    if (!grid || !grid.parentNode) return;

    const error = document.createElement('div');
    error.className = 'infinite-scroll-error';
    error.innerHTML = '<p>Kunne ikke laste flere produkter. <a href="' + nextPageUrl + '">Prøv igjen</a></p>';
    error.style.textAlign = 'center';
    error.style.padding = '20px';
    error.style.color = '#d00';
    
    grid.parentNode.insertBefore(error, grid.nextSibling);
  }

  /**
   * Show end of products message
   */
  function showEndMessage() {
    const grid = document.querySelector('.collection-grid__wrapper');
    if (!grid || !grid.parentNode) return;

    const end = document.createElement('div');
    end.className = 'infinite-scroll-end';
    end.innerHTML = '<p style="text-align: center; padding: 40px 0; color: #999; font-size: 14px;">Du har sett alle produktene</p>';
    
    grid.parentNode.insertBefore(end, grid.nextSibling);

    // Disconnect observer since we're done
    if (observer) {
      observer.disconnect();
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose API for theme integration
  window.infiniteScroll = {
    reload: init,
    isLoading: function() { return isLoading; },
    hasMore: function() { return hasMorePages; }
  };

})();
